/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  useChangePassword,
  useLogout,
} from 'app/core/api/auth/api-auth';
import { isKeyedAxiosError } from 'app/core/api/api-core/api-core.types';
import Button from 'app/shared/components/button/Button';
import TextInput from 'app/shared/components/form-input/TextInput';
import { PW_RULES } from 'app/shared/components/form-input/utilities/form-tools';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import { ErrorText, PadExpand } from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import Heading from 'app/shared/components/typography/heading/Heading';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import Form from 'app/shared/frames/Form';
import { BLANK$ } from 'app/tools/utils';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Observable } from 'rxjs';
import { switchMapTo } from 'rxjs/operators';

type FormValues = {
  oldPassword?: string;
  newPassword1: string;
  newPassword2: string;
};

type Props = {
  forceReset?: boolean;
};
export default function CreateNewPassword({
  forceReset,
}: Props): JSX.Element {
  const { showSnack } = useShowInfo();
  const [errorResponse, setErrorResponse] = useState('');
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { control, errors, handleSubmit, setValue, getValues } =
    useForm<FormValues>({
      mode: 'onTouched',
    });

  const {
    request$: submitChangePassword$,
    sending: submittingNewPassword,
    error: changePasswordError,
    clearError: clearChangePasswordError,
  } = useChangePassword();

  const {
    request$: logout$,
    error: logoutError,
    clearError: clearLogoutError,
  } = useLogout();

  const onSubmit = ({ oldPassword, ...newPasswords }: FormValues) => {
    // NOTE: manually clearing both here to ensure error always
    // disappears at least for a split second when submit is pressed
    clearChangePasswordError.current();
    clearLogoutError.current();
    setErrorResponse('');

    const action$: Observable<unknown> = (() => {
      if (forceReset) {
        return BLANK$; // left in for future functionality
      } else {
        if (!oldPassword) throw new Error('old password missing');
        return submitChangePassword$({
          oldPassword,
          ...newPasswords,
        });
      }
    })();

    action$.pipe(switchMapTo(logout$())).subscribe({
      complete: () => {
        showSnack({
          message:
            'Success! Please log in again with your new password',
          alertType: 'info',
        });
      },
      error: (e: unknown) => {
        if (isKeyedAxiosError(e)) {
          if (e.response?.status === 400) {
            setErrorResponse(
              'Your old password was entered incorrectly. Please enter it again.'
            );
          }
        }
        setValue('oldPassword', '');
        setValue('newPassword1', '');
        setValue('newPassword2', '');
      },
    });
  };

  return (
    <>
      {forceReset && <PadExpand proportion={1.2} />}
      <Icon icon={IconLib.KEY_BOXED} soloContentStyle />
      {forceReset && (
        <Heading color="primary">Create new password</Heading>
      )}

      <Body antiMargin>
        {forceReset ? (
          <>
            You logged in with your temporary password - please setup
            a new password for this account.
          </>
        ) : (
          <>
            Please re-enter your old password, then pick a new one and
            enter it below
          </>
        )}
      </Body>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {!forceReset && (
          <TextInput
            placeholder="Old password"
            autoComplete="current-password"
            type="password"
            icon={IconLib.CLOCK}
            errorText={errors.oldPassword?.message}
            controller={{
              control,
              name: 'oldPassword',
              rules: {
                required: 'Required',
              },
            }}
          />
        )}
        <TextInput
          placeholder="New password"
          autoComplete="new-password"
          type="password"
          icon={IconLib.LOCK}
          errorText={errors.newPassword1?.message}
          controller={{
            control,
            name: 'newPassword1',
            rules: PW_RULES,
          }}
        />
        <TextInput
          placeholder="Confirm new password"
          autoComplete="new-password"
          type="password"
          icon={IconLib.LOCK}
          errorText={errors.newPassword2?.message}
          controller={{
            control,
            name: 'newPassword2',
            rules: {
              required: 'Required',
              validate: {
                matchesPassword: (text: string) =>
                  text === getValues('newPassword1') ||
                  'Both new password entries must match',
              },
            },
          }}
        />
        <ErrorText message={changePasswordError || logoutError} />
        <ErrorText message={errorResponse} />
        <Button type="submit" pending={submittingNewPassword}>
          Reset password
        </Button>
      </Form>
      {forceReset && (
        <>
          <PadExpand proportion={2} />
          <Button
            variant="text"
            slim
            color="default"
            onClick={() => logout$().subscribe()}
          >
            Log out
          </Button>
        </>
      )}
    </>
  );
}
